import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import { Loader } from "./components/KpiCard";

import Login from "./pages/Login";
import ControlCenter from "./pages/ControlCenter";
import ChartOfAccounts from "./pages/ChartOfAccounts";
import GeneralLedger from "./pages/GeneralLedger";
import Budget from "./pages/Budget";
import Funds from "./pages/Funds";
import Revenue from "./pages/Revenue";
import Expenses from "./pages/Expenses";
import Grants from "./pages/Grants";
import Donors from "./pages/Donors";
import Vendors from "./pages/Vendors";
import Programs from "./pages/Programs";
import Reports from "./pages/Reports";
import BoardMeetings from "./pages/BoardMeetings";
import Insights from "./pages/Insights";
import BankReconciliation from "./pages/BankReconciliation";
import Documents from "./pages/Documents";
import UserManagement from "./pages/UserManagement";
import AuditTrail from "./pages/AuditTrail";
import Payroll from "./pages/Payroll";
import ComplianceRisk from "./pages/ComplianceRisk";
import Governance from "./pages/Governance";
import Strategy from "./pages/Strategy";
import FixedAssets from "./pages/FixedAssets";
import Investments from "./pages/Investments";
import Receivables from "./pages/Receivables";
import Payables from "./pages/Payables";
import GoogleCallback from "./pages/GoogleCallback";
import Portal from "./pages/Portal";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loader label="Authenticating…" />;
  if (!user) return <Navigate to="/portal" replace />;
  if (roles && !roles.includes(user.role)) {
    return (
      <Layout>
        <div className="text-center py-24">
          <div className="font-heading text-2xl text-[#1A1A1A]">Access Restricted</div>
          <p className="text-sm text-[#5C5C54] mt-2">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const routes = [
    ["/", <ControlCenter />],
    ["/accounts", <ChartOfAccounts />],
    ["/ledger", <GeneralLedger />],
    ["/budget", <Budget />],
    ["/funds", <Funds />],
    ["/revenue", <Revenue />],
    ["/expenses", <Expenses />],
    ["/grants", <Grants />],
    ["/donors", <Donors />],
    ["/vendors", <Vendors />],
    ["/programs", <Programs />],
    ["/reports", <Reports />],
    ["/board-meetings", <BoardMeetings />],
    ["/bank-reconciliation", <BankReconciliation />],
    ["/documents", <Documents />],
    ["/payroll", <Payroll />],
    ["/compliance", <ComplianceRisk />],
    ["/governance", <Governance />],
    ["/strategy", <Strategy />],
    ["/assets", <FixedAssets />],
    ["/investments", <Investments />],
    ["/receivables", <Receivables />],
    ["/payables", <Payables />],
    ["/admin/users", <UserManagement />, ["admin"]],
    ["/admin/audit", <AuditTrail />, ["admin", "auditor"]],
    ["/insights", <Insights />],
  ];
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal" element={<Portal />} />
      {routes.map(([path, el, roles]) => (
        <Route key={path} path={path} element={<Protected roles={roles}>{el}</Protected>} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return (
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
            <GoogleCallback />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </div>
    );
  }
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
